"use client";

import { Building2, Check, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import type { PrismScope } from "@/lib/prism/types";

interface CompanyScopePickerProps {
    scope: PrismScope;
    onScopeChange: (s: PrismScope) => void;
}

/**
 * Fund / Company context switch. When "Company" is active, a dropdown lists
 * every portfolio company so the user picks which one Prism focuses on. The
 * selection is shared app-wide via AuthContext.activeCompany.
 */
export function CompanyScopePicker({ scope, onScopeChange }: CompanyScopePickerProps) {
    const { companies, activeCompany, switchActiveCompany } = useAuth();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function onDoc(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        }
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    return (
        <div className="flex items-center gap-3 px-4 py-2.5 border-b border-[var(--border-hairline)] bg-surface-0 shrink-0">
            <span className="text-[11px] font-semibold text-ink-3 uppercase tracking-wide">
                Context
            </span>
            <div className="inline-flex rounded-lg border border-[var(--border-hairline)] p-0.5 bg-surface-1">
                <button
                    type="button"
                    onClick={() => onScopeChange("fund")}
                    className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                        scope === "fund" ? "bg-card text-accent shadow-[var(--shadow-card)]" : "text-ink-2"
                    }`}
                >
                    Fund
                </button>
                <button
                    type="button"
                    onClick={() => onScopeChange("company")}
                    className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                        scope === "company" ? "bg-card text-accent shadow-[var(--shadow-card)]" : "text-ink-2"
                    }`}
                >
                    Company
                </button>
            </div>

            {scope === "company" ? (
                <div className="relative" ref={ref}>
                    <button
                        type="button"
                        onClick={() => setOpen((o) => !o)}
                        className="flex items-center gap-1.5 rounded-lg border border-[var(--border-hairline)] bg-surface-0 px-2.5 py-1.5 text-xs font-medium text-ink-0 hover:bg-surface-1 transition-colors"
                    >
                        <Building2 className="w-3.5 h-3.5 text-accent" />
                        {activeCompany?.name ?? "Select company"}
                        <ChevronDown className="w-3.5 h-3.5 text-ink-3" />
                    </button>
                    {open ? (
                        <div className="absolute top-9 left-0 z-30 w-60 rounded-xl border border-[var(--border-hairline)] bg-surface-0 shadow-xl py-1 text-sm max-h-72 overflow-y-auto">
                            {companies.length === 0 ? (
                                <p className="px-3 py-2 text-xs text-ink-3">
                                    No companies yet. Add one in your portfolio.
                                </p>
                            ) : (
                                companies.map((c) => (
                                    <button
                                        key={c.id}
                                        type="button"
                                        onClick={() => {
                                            switchActiveCompany(c.id);
                                            setOpen(false);
                                        }}
                                        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-surface-1"
                                    >
                                        <span className="w-4">
                                            {activeCompany?.id === c.id ? (
                                                <Check className="w-4 h-4 text-accent" />
                                            ) : null}
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block text-ink-0 truncate">{c.name}</span>
                                            <span className="block text-xs text-ink-3 truncate">
                                                {c.industry}
                                                {c.country ? ` · ${c.country}` : ""}
                                            </span>
                                        </span>
                                    </button>
                                ))
                            )}
                        </div>
                    ) : null}
                </div>
            ) : null}

            {scope === "company" && activeCompany ? (
                <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-accent-bg text-accent px-2.5 py-1 text-xs font-medium ring-1 ring-accent-line">
                    Focused on {activeCompany.name}
                </span>
            ) : null}
        </div>
    );
}
